import { RefObject, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CellRendererProps, FlatList, StyleSheet, useWindowDimensions } from 'react-native';
import { useAnimatedReaction, useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BottomFooterRG } from '@/components/BottomFooterRG';
import { LeftSideTabBar } from '@/components/LeftSideTabBar';
import { Loading } from '@/components/Loading';
import { NestedScrollableTabBar } from '@/components/NestedScrollableTabBar';
import { EVENT_SHEET_TITLE_HEIGHT } from '@/components/event-sheet/EventSheetTitle';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { TraditionalMatchupCard } from '@/components/sbk-traditional-matchup-card/TraditionalMatchupCard';
import { isPopularMarket } from '@/components/sbk-traditional-matchup-card/utils';
import { MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { useScrollableModalContext } from '@/feature/circular-pager/ScrollableModalContextProvider';
import { EventHud } from '@/feature/event-details-sbk/components/EventHud';
import { INITIAL_EXPAND_ACCORDION_COUNT } from '@/feature/event-details-sbk/components/market-page-layouts/constants';
import { MarketAccordion } from '@/feature/event-details-sbk/components/market-page-layouts/market-accordion-layout/MarketAccordion';
import { MicroMarketAccordion } from '@/feature/event-details-sbk/components/market-page-layouts/micro-market-layout/MicroMarketAccordion';
import PlayerProp from '@/feature/event-details-sbk/components/market-page-layouts/player-props-layout/PlayerProp';
import { Event, MarketCategory } from '@/feature/event-details-sbk/types';
import { Market } from '@/feature/event-details-sbk/types';
import { MarketsUnavailable } from '@/feature/league-hub-sbk/components/MarketsUnavailable';
import { EventItemSkeleton } from '@/feature/lobby-sbk/components/EventListSkeleton';
import { PICK_SLIP_BAR_HEIGHT } from '@/feature/lobby-sbk/components/PickSlipBar/PickSlipBar';
import { StickyCellRendererComponent } from '@/feature/lobby/components/sticky-item/StickyCellRendererComponent';
import { StickyWrapper } from '@/feature/lobby/hooks/use-sticky-translation';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useSbkHudHeight } from '@/feature/sbk-event-sheets/hooks/use-sbk-hud-height';
import { getMarketCategoryKey } from '@/feature/sbk-event-sheets/utils/get-market-category-key';
import { isWeb } from '@/utils/constants-platform-specific';
import { BottomSheetFlatList, BottomSheetFlatListMethods } from '@gorhom/bottom-sheet';

import { useEventContent } from '../hooks/use-event-content';
import { PlayerPropsFilters } from './PlayerPropsFilters';

const ListComponent = isWeb ? FlatList : BottomSheetFlatList;

const isMarket = (item: string | MarketCategory | Market): item is Market =>
    typeof item === 'object' && 'market_type' in item;

const isMarketCategory = (item: string | MarketCategory | Market): item is MarketCategory =>
    typeof item === 'object' && 'markets' in item;

const keyExtractor = (item: string | MarketCategory | Market) => {
    if (typeof item === 'string') {
        return item;
    }
    if (isMarketCategory(item)) {
        return `market_category-${getMarketCategoryKey(item)}`;
    }
    return `market-${item.id}`;
};

export const SbkEventSheetContent = ({ eventId, isActive }: { eventId: string; isActive: boolean }) => {
    const { t } = useTranslation('league_hub_sbk');
    const {
        tabs,
        event,
        updateSelectedTab,
        selectedTab,
        marketCategories,
        itemsToRender,
        initialExpandedMarkets,
        selectedMarketCategory,
        updateSelectedMarketCategory,
        marketLayoutType,
        isLoading,
        hasPublishedMarkets,
        isFetched,
        eventWithPopularMarkets,
    } = useEventContent({
        eventId,
        isActive,
    });
    const insets = useSafeAreaInsets();
    const { height } = useWindowDimensions();

    const tabsStickPosition = useSharedValue(0);
    const listScrollOffset = useSharedValue(0);

    const { scrollOffset, sheetScrollableHook } = useScrollableModalContext();

    const listRef = useRef<FlatList>(null);
    const scrollToTabs = useCallback(() => {
        if (listScrollOffset.value > tabsStickPosition.value) {
            listRef?.current?.scrollToOffset({
                animated: false,
                offset: tabsStickPosition.value + 1, // + 1 to make sure the header displays the game time on event page
            });
        }
    }, [tabsStickPosition.value, listScrollOffset.value]);

    // Scroll to tabs when tab is changed and scrolled down
    const updateTabs = useCallback(
        (tab: string) => {
            if (isActive) {
                updateSelectedTab(tab);
                scrollToTabs();
            }
        },
        [isActive, scrollToTabs, updateSelectedTab]
    );

    // Scroll to tabs when player props category is changed and scrolled down
    const updateMarketCategory = useCallback(
        (marketCategory: MarketCategory) => {
            if (isActive) {
                updateSelectedMarketCategory(marketCategory);
                scrollToTabs();
            }
        },
        [isActive, scrollToTabs, updateSelectedMarketCategory]
    );

    // Scroll to top when event changes
    const previousIsActive = useRef(isActive);
    useEffect(() => {
        if (!isActive && previousIsActive.current) {
            listRef.current?.scrollToOffset({ offset: 0, animated: false });
        }
        previousIsActive.current = isActive;
    }, [eventId, isActive, listRef]);

    useAnimatedReaction(
        () => scrollOffset.value,
        current => {
            listScrollOffset.value = current;
        }
    );

    const containerStyle = useMemo(() => {
        return {
            // web doesn't have top and bottom insets, so we use a fixed padding so the last item is not cut off
            paddingBottom: (isWeb ? 140 : insets.bottom + insets.top) + 12 + PICK_SLIP_BAR_HEIGHT,
            paddingTop: EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT,
            minHeight: height,
        };
    }, [insets.bottom, insets.top, height]);

    // Using a custom cell renderer component to make sure the tabs are rendered above the other items
    // because we stick the tabs to the top of the screen
    // and we need to make sure the other items are rendered below it
    const CellRendererComponent = useCallback(
        ({ children, style, ...props }: CellRendererProps<any>) => {
            const renderingTabs = props.cellKey === 'tabs';
            const adjustedStyle = {
                style,
                zIndex: renderingTabs ? 100 : undefined,
                top: 0,
            };
            // StickyCellRendererComponent doesn't work properly on web, only part of the player list is rendering
            // Best solution for now is to remove the sticky tabs on web (players/projections)
            return isWeb ? (
                <Box style={style} {...props}>
                    {children}
                </Box>
            ) : (
                <StickyCellRendererComponent
                    {...props}
                    shouldBeSticky={renderingTabs}
                    style={adjustedStyle}
                    onLayout={e => {
                        if (renderingTabs) {
                            // tabs / player props filters should stick below the event sheet title
                            tabsStickPosition.value =
                                e.nativeEvent.layout.y - MODAL_HANDLE_HEIGHT - EVENT_SHEET_TITLE_HEIGHT;
                        }
                        props.onLayout?.(e);
                    }}
                >
                    {children}
                </StickyCellRendererComponent>
            );
        },
        [tabsStickPosition]
    );

    const renderItem = useCallback(
        ({ item, index }: { item: string | MarketCategory | Market; index: number }) => {
            if (!event) {
                return null;
            }
            if (item === 'tabs') {
                return (
                    <StickyWrapper initialPosition={tabsStickPosition} scrollOffset={scrollOffset}>
                        {tabs.length > 0 ? (
                            <NestedScrollableTabBar>
                                <LeftSideTabBar
                                    tabs={tabs}
                                    initialIndex={0}
                                    setSelectedTab={updateTabs}
                                    location="event"
                                    tabBarStyle={styles.tabBarStyle}
                                    contentContainerStyle={styles.contentContainerStyle}
                                />
                            </NestedScrollableTabBar>
                        ) : null}
                        {marketLayoutType === 'player_props' && selectedMarketCategory ? (
                            <Box backgroundColor={ds.colorToken.background.neutral.base}>
                                <PlayerPropsFilters
                                    marketCategories={marketCategories}
                                    event={event}
                                    updateSelectedCategory={updateMarketCategory}
                                    selectedCategory={selectedMarketCategory}
                                />
                            </Box>
                        ) : null}
                    </StickyWrapper>
                );
            }

            if (item === 'matchup_card') {
                if (!hasPublishedMarkets && isFetched && !isLoading) {
                    // If there are no published markets at all, we don't want to render the matchup card
                    return null;
                }
                return (
                    <Box backgroundColor={ds.colorToken.background.neutral.base} paddingTop="s24">
                        {!isFetched || isLoading || !eventWithPopularMarkets ? (
                            <EventItemSkeleton showFooter={false} sportName={event?.sport?.name} />
                        ) : (
                            <TraditionalMatchupCard variant="static" event={eventWithPopularMarkets} showHeader />
                        )}
                    </Box>
                );
            }

            if (marketLayoutType === 'micro_market' && typeof item === 'string') {
                return (
                    <Box
                        backgroundColor={ds.colorToken.background.neutral.base}
                        paddingHorizontal="s16"
                        paddingTop={index === 1 ? 's16' : 's0'}
                    >
                        <MicroMarketAccordion
                            microMarketId={item}
                            event={event}
                            isExpanded={index - 1 < INITIAL_EXPAND_ACCORDION_COUNT}
                        />
                    </Box>
                );
            }

            if (marketLayoutType === 'player_props' && selectedMarketCategory && isMarket(item)) {
                return (
                    <Box
                        backgroundColor={ds.colorToken.background.neutral.base}
                        paddingHorizontal="s16"
                        paddingTop={index === 1 ? 's16' : 's0'}
                    >
                        <PlayerProp
                            market={item}
                            event={event}
                            rowIndex={index - 1}
                            rowCount={selectedMarketCategory.markets.length}
                        />
                    </Box>
                );
            }

            if (marketLayoutType === 'market_accordion' && isMarketCategory(item)) {
                const isGameLines = selectedTab?.key === 'game_lines';
                const isPopularMarketType = isPopularMarket(item.market_type, event?.sport?.name);
                return (
                    <Box
                        backgroundColor={ds.colorToken.background.neutral.base}
                        paddingHorizontal="s16"
                        paddingTop={!isGameLines && index === 1 ? 's16' : 's0'}
                    >
                        <MarketAccordion
                            marketCategory={item}
                            event={event}
                            isExpanded={isGameLines ? false : initialExpandedMarkets.includes(item.market_type)}
                            appendAlt={isPopularMarketType} // If the market is one of the popular markets, we still want to show it here but we call it "Alt marketName"
                        />
                    </Box>
                );
            }

            return null;
        },
        [
            tabsStickPosition,
            scrollOffset,
            tabs,
            event,
            selectedTab,
            updateTabs,
            marketCategories,
            marketLayoutType,
            initialExpandedMarkets,
            selectedMarketCategory,
            updateMarketCategory,
            isLoading,
            isFetched,
            hasPublishedMarkets,
            eventWithPopularMarkets,
        ]
    );

    const renderFooter = useCallback(() => {
        if (!isFetched && !isLoading && !hasPublishedMarkets) {
            return null;
        }

        if (isLoading) {
            return (
                <Box m={'s46'}>
                    <Loading />
                </Box>
            );
        } else if (!hasPublishedMarkets && isFetched) {
            return (
                <>
                    <Box paddingHorizontal="s32">
                        <MarketsUnavailable
                            offHeight={200}
                            description={t('category_markets_unavailable_description')}
                        />
                    </Box>
                    <Box paddingHorizontal="s16">
                        <BottomFooterRG footerMarginTop="s64" />
                    </Box>
                </>
            );
        } else {
            return (
                <Box paddingHorizontal="s16">
                    <BottomFooterRG footerMarginTop="s64" />
                </Box>
            );
        }
    }, [isLoading, hasPublishedMarkets, t, isFetched]);

    const renderFooterComponent = useCallback(() => {
        // Do not render the footer if the page is not active, to prevent UI jumps
        if (isActive) {
            return renderFooter();
        } else {
            return null;
        }
    }, [isActive, renderFooter]);

    if (!event) {
        return null;
    }

    return (
        <Box flex={1} minHeight={'100%'}>
            <ListComponent
                ListHeaderComponent={<ListHeaderComponent event={event} />}
                data={itemsToRender}
                renderItem={renderItem}
                ListFooterComponent={renderFooterComponent}
                windowSize={3}
                maxToRenderPerBatch={10}
                keyExtractor={keyExtractor}
                CellRendererComponent={CellRendererComponent}
                contentContainerStyle={containerStyle}
                ref={listRef as RefObject<BottomSheetFlatListMethods>}
                showsVerticalScrollIndicator={false}
                scrollEventsHandlersHook={sheetScrollableHook}
                // needed for sticky components to work on Android
                removeClippedSubviews={false}
            />
        </Box>
    );
};

const ListHeaderComponent = memo(({ event }: { event: Event }) => {
    const { showScoreboard, hudHeight } = useSbkHudHeight(event);
    return (
        <MaxWidthWrapper>
            <EventHud event={event} hudHeight={hudHeight} showScoreboard={showScoreboard} />
        </MaxWidthWrapper>
    );
});

const styles = StyleSheet.create({
    tabBarStyle: {
        marginHorizontal: 0,
    },
    contentContainerStyle: {
        paddingHorizontal: 16,
    },
});
